<template>
	<div class="ccl-accordion-list">
		<div class="ccl-accordion-list__header">
			<Search
				class="ccl-accordion-list__header-search"
				variant="toggle-icons"
				:placeholder="
					'Search by client name/#, company, or sub location'
				"
				v-model="search"
			/>
			<Button
				:disabled="!disabledCollapseAllButton"
				:btnClick="collapseAll"
				labelText="Collapse All"
				class="ccl-accordion-list__collapse"
				className="btn--link"
			/>
		</div>
		<ScrollContainer
			v-if="hasItems"
			variant="infinity-scroll"
			:hasMoreItems="false"
			:showEndResultByDefault="false"
			:takeFixedHeight="fixedHeight"
		>
			<ClientList
				:clients="itemsData"
				@change="updateItemsData"
				@client-updated="updateAllItems"
				:search="search"
				ref="clientList"
			/>
		</ScrollContainer>
		<NoResults v-if="!hasItems" :variant="'condensed'" />
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import ClientList from '@/components/ccl-selector/ccl-left-side/client-list.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Search from '@/components/forms/search/search';
import NoResults from '@/site-components/no-results/no-results';
// vuex
import { mapState } from 'vuex';

// functions
import _ from 'lodash';

export default {
	name: 'CclAccordion',
	components: {
		Button,
		ClientList,
		ScrollContainer,
		Search,
		NoResults
	},
	props: {
		items: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			search: '',
			itemsData: [],
			copyItems: [],
			clientsWithActiveBetaFeature: [],
			fixedHeight: { height: 100, unit: '%' }
		};
	},
	created() {
		this.itemsData = _.cloneDeep(this.items);
		this.copyItems = _.cloneDeep(this.items);
		this.clientsWithActiveBetaFeature = this.filterClientsWithActiveBetaFeature();
	},
	computed: {
		...mapState('features', ['clientsAccordionStatuses']),
		hasItems() {
			return _.get(this, 'itemsData.length', 0) > 0;
		},
		disabledCollapseAllButton() {
			return (
				this.clientsAccordionStatuses.length > 0 &&
				this.itemsData.length > 0
			);
		}
	},
	methods: {
		collapseNode(node) {
			if (node.isOpenStatus) {
				node.collapse();
			}

			for (const children of node.$children) {
				if (children) {
					this.collapseNode(children);
				}
			}
		},
		collapseAll() {
			this.$refs.clientList.$children.forEach(element => {
				this.collapseNode(element);
			});
			this.$store.commit('features/clearAccordionStatuses');
		},
		updateItemsData(value) {
			this.itemsData = value;
			this.$emit('change', value);
		},
		updateAllItems(value) {
			const index = this.copyItems.findIndex(
				client => client.id === value.client.id
			);
			if (index !== -1) {
				this.$set(this.copyItems, index, value.client);
				this.clientsWithActiveBetaFeature = this.filterClientsWithActiveBetaFeature();
			}
		},
		filterItemsBySearch() {
			const query = this.search.toLowerCase();
			const filteredClients = this.items.filter(client => {
				return (
					(client.customerName || '').toLowerCase().includes(query) ||
					(client.customerNumber &&
						client.customerNumber.toString().includes(query)) ||
					client.companies.some(
						company =>
							(company.companyName || '')
								.toLowerCase()
								.includes(query) ||
							company.locations.some(location =>
								(location.locationName || '')
									.toLowerCase()
									.includes(query)
							)
					)
				);
			});
			this.itemsData = filteredClients;
		},
		filterClientsWithActiveBetaFeature() {
			let filteredClients = [];
			if (!_.isEmpty(this.copyItems)) {
				filteredClients = this.copyItems.filter(
					client =>
						client.activeBetaFeature ||
						client.companies.some(
							company =>
								company.activeBetaFeature ||
								company.locations.some(
									location => location.activeBetaFeature
								)
						)
				);
			}
			this.$emit('active-clients', filteredClients);
			return filteredClients;
		}
	},
	watch: {
		immediate: true,
		search: {
			handler(value) {
				if (value) {
					this.filterItemsBySearch();
				} else {
					this.itemsData = this.items;
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.ccl-accordion-list {
	border: 1px solid $neutral-3;
	padding: $padding-md;
	background-color: $white;
	border-radius: $border-radius;
	flex-direction: column;

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: $gutter-lg;
		margin-bottom: $gutter-xl;

		&-search {
			width: 450px;
		}
	}
}
</style>
